import { frameId } from "../assets/manifest.ts";
import { activationOf, footprintOf, frameSizeOf, initialFootprint, resolvable, thingAnchor, thingFeet } from "../compile/behaviour.ts";
import type { CompileContext, CompileObjectType, FrameDimensions } from "../compile/context.ts";
import { frameCellSpace, stampFootprint, type PropFootprint } from "../compile/footprint.ts";
import { COLLISION_CELLS_PER_TILE, type MapDocument, type PropBehaviour, type PropInstance, type TileRef } from "../document/types.ts";
import type { ValidationIssue } from "./adapter.ts";
import type { FrameCell } from "./footprint-brush.ts";
import { propFeet } from "./objects.ts";
import { presenceArea, type PresenceArea } from "./presence-area.ts";
import { propPresenceArea, type FrameRectOf } from "./stand-in-presence.ts";

/**
 * A behaviour prop's activation cells where it stands on the map (P1.6-T32, ADR-070; the brief's 6.3 item 4
 * and 6.4), laid out as the game tests them (ADR-071's principle). A thing's cells are the compile's for the
 * state the map shows (`activationOf`: the painted cells, else the default ring spanning every state's
 * footprint, mirrored across that state's frame on `flipX`), laid from the anchor that state's frame is drawn
 * at (`thingAnchor`, ADR-040 amendment of 2026-10-04), with the frame's corner on the cell corner its
 * footprint is laid from (`frameCellSpace`), the rule `inActivation` reads. A prop driven
 * by presence has one area on every state, placed from its anchor (`propPresenceArea`), where a cell
 * straddling two world cells covers both, as in the game. Either resolves into the bitmap of the world
 * collision cells it covers (`presenceArea`). The footprint drawn beside them is the state's as
 * the collision grid stamps it, and a pixel on the map reads back as the stored cell under it in the same
 * space (`frameCellAt`), which Edit on map paints through.
 */

/**
 * Where a prop's stored `[column, rowsUp]` cells lie on the map: the frame's left edge and base in world
 * pixels, how many cell columns its frame spans, and whether the columns are mirrored across them.
 */
export interface CellSpace {
  readonly left: number;
  readonly bottom: number;
  readonly columns: number;
  readonly mirrored: boolean;
}

/**
 * A behaviour prop's activation on the map: the state laid out, the world cells its activation covers,
 * whether those are its default cells (none painted), its feet point in world pixels and the space its
 * stored cells lie in.
 */
export interface ActivationLook {
  readonly state: string;
  readonly cells: PresenceArea;
  readonly defaulted: boolean;
  readonly feet: { readonly x: number; readonly y: number };
  readonly space: CellSpace;
}

/**
 * One behaviour prop of the Activation overlay: where it is, its type, its worst issue and its look.
 */
export interface ActivationEntry {
  readonly layerId: string;
  readonly propId: string;
  readonly type: string;
  readonly issue: "error" | "warning" | null;
  readonly look: ActivationLook;
}

/**
 * What the overlay's entries are resolved from: the map, the compile context, the asset manifest's frame
 * rects, the tile size, the state the map shows each prop in (null for its initial state), the map's issues
 * and whether a layer is shown.
 */
export interface ActivationSources {
  readonly map: MapDocument;
  readonly context: CompileContext;
  readonly rectOf: FrameRectOf;
  readonly tileSize: number;
  readonly shownState: (layerId: string, prop: PropInstance) => string | null;
  readonly issues: readonly ValidationIssue[];
  readonly shown: (layerId: string) => boolean;
}

/**
 * A state of a prop's type that shows a frame: the initial state the prop's own frame, a further state the
 * frame its behaviour gives it.
 */
interface StateFrame {
  readonly state: CompileObjectType["states"][number];
  readonly frame: TileRef;
  readonly frameId: string;
  readonly size: FrameDimensions | undefined;
}

const NOWHERE: PresenceArea = { x0: 0, y0: 0, width: 0, height: 0, cells: new Uint8Array(0) };

/**
 * The cells of margin the activation editors draw around a prop's frame, and so the farthest from its art a
 * stroke paints, on the map as in the cell editors.
 */
export const ACTIVATION_MARGIN = 2;

/**
 * The prop's type as the compile context registers it.
 *
 * @param prop The prop.
 * @param context The compile context.
 * @returns The type, or undefined for a prop with no behaviour or an unregistered type.
 */
function typeOf(prop: PropInstance, context: CompileContext): CompileObjectType | undefined {
  const behaviour = prop.behaviour;
  return behaviour === undefined ? undefined : context.objectTypes.find((t) => t.type === behaviour.type);
}

/**
 * The states of a type that show a frame, in the type's order; a further state the behaviour gives no frame is
 * left out, as the editor's default ring leaves it out (`initialDefaultActivation`).
 *
 * @param prop The prop.
 * @param type Its type.
 * @param context The compile context, which sizes the frames.
 * @returns The states.
 */
function stateFrames(prop: PropInstance, type: CompileObjectType, context: CompileContext): StateFrame[] {
  return type.states.flatMap((state) => {
    const frame = state.id === type.initial ? prop.frame : prop.behaviour?.frames?.[state.id];
    if (frame === undefined) {
      return [];
    }
    const id = frameId(frame.tileset, null, frame.tile);
    return [{ state, frame, frameId: id, size: frameSizeOf(context, id) }];
  });
}

/**
 * The footprint a state stamps, or null when it stamps nothing or its cells cannot be laid on its frame.
 *
 * @param prop The prop.
 * @param shown The state.
 * @param context The compile context.
 * @param cellPixels Pixels per collision cell.
 * @returns The footprint.
 */
function stateFootprint(prop: PropInstance, shown: StateFrame, context: CompileContext, cellPixels: number): PropFootprint | null {
  return resolvable(shown.state, shown.size) ? footprintOf(prop, shown.state, shown.frameId, context.collision.footprints?.[shown.frameId], shown.size, cellPixels) : null;
}

/**
 * A behaviour prop's activation on the map in a state.
 *
 * @param prop The placed prop.
 * @param stateId The state the map shows it in, or null for its initial state.
 * @param context The compile context.
 * @param rectOf The asset manifest's frame rects, which a presence prop's default area is measured from.
 * @param tileSize Tile size in pixels.
 * @returns The look, or null for a prop with no behaviour, an unregistered type, a state the behaviour gives no
 *   frame, or cells the compile could not lay out (a frame no pack sizes).
 * @example activationLook(sign, null, context, rectOf, 32) lays a sign's painted cells from its frame's bottom-left.
 */
export function activationLook(prop: PropInstance, stateId: string | null, context: CompileContext, rectOf: FrameRectOf, tileSize: number): ActivationLook | null {
  const type = typeOf(prop, context);
  if (type === undefined) {
    return null;
  }
  const frames = stateFrames(prop, type, context);
  const initial = frames.find((f) => f.state.id === type.initial);
  const shown = frames.find((f) => f.state.id === (stateId ?? type.initial));
  if (initial === undefined || shown === undefined) {
    return null;
  }
  const cellPixels = tileSize / COLLISION_CELLS_PER_TILE;
  const authored = prop.behaviour?.activation;
  if (type.inside !== undefined) {
    const placedFeet = propFeet(prop, initial.size?.w ?? 0, tileSize);
    const feet = { x: placedFeet.x * tileSize, y: placedFeet.y * tileSize };
    const rect = rectOf(initial.frameId);
    const area = propPresenceArea(prop, rect, initial.size, tileSize);
    if (area === null || rect === undefined) {
      return null;
    }
    const columns = Math.ceil((initial.size?.w ?? rect.w) / cellPixels);
    return { state: shown.state.id, cells: area, defaulted: (authored ?? []).length === 0, feet, space: { left: prop.x * tileSize, bottom: (prop.y + 1) * tileSize, columns, mirrored: prop.flipX } };
  }
  if (!resolvable(shown.state, shown.size) || (authored !== undefined && shown.size === undefined)) {
    return null;
  }
  const feetTiles = thingFeet(prop, initial.size?.w ?? 0, context, tileSize);
  const feet = { x: feetTiles.x * tileSize, y: feetTiles.y * tileSize };
  const footprint = stateFootprint(prop, shown, context, cellPixels);
  const others = frames.filter((f) => f !== shown).map((f) => stateFootprint(prop, f, context, cellPixels));
  const cells = activationOf(prop, { id: shown.state.id, frameId: shown.frameId, size: shown.size, footprint }, others, tileSize);
  const width = shown.size?.w ?? 0;
  const anchor = thingAnchor(prop, context, tileSize, width);
  const mirrored = prop.flipX && shown.size !== undefined;
  const laid = frameCellSpace(anchor.x * tileSize, (anchor.y + 1) * tileSize, width, mirrored, cellPixels);
  return {
    state: shown.state.id,
    cells: presenceArea({ x: laid.left / tileSize, y: laid.bottom / tileSize - 1 }, cells),
    defaulted: authored === undefined,
    feet,
    space: { left: laid.left, bottom: laid.bottom, columns: Math.ceil(width / cellPixels), mirrored },
  };
}

/**
 * A footprint stamped where a placement puts it, as the bitmap of the world collision cells it blocks. The
 * stamp runs on a scratch grid a whole number of tiles off the world's, so every cell lands exactly where the
 * world grid's stamp would put it, without clipping at the map's edge.
 *
 * @param footprint The footprint.
 * @param placement Its anchor in tiles and mirroring.
 * @param tileSize Tile size in pixels.
 * @returns The area.
 */
function stampedArea(footprint: PropFootprint, placement: { readonly x: number; readonly y: number; readonly flipX: boolean; readonly flipY: boolean }, tileSize: number): PresenceArea {
  const cells = COLLISION_CELLS_PER_TILE;
  const tilesWide = Math.ceil(footprint.w / tileSize) + 2;
  const tilesHigh = Math.ceil(footprint.h / tileSize) + 2;
  const ox = Math.floor(placement.x) - 1;
  const oy = Math.floor(placement.y) + 2 - tilesHigh;
  const width = tilesWide * cells;
  const grid = new Uint8Array(width * tilesHigh * cells);
  stampFootprint(grid, tilesWide, tilesHigh, footprint, { x: placement.x - ox, y: placement.y - oy, flipX: placement.flipX, flipY: placement.flipY }, tileSize, cells, 1);
  const blocked: (readonly [number, number])[] = [];
  for (let i = 0; i < grid.length; i += 1) {
    if (grid[i] !== 0) {
      blocked.push([i % width, Math.floor(i / width)]);
    }
  }
  if (blocked.length === 0) {
    return NOWHERE;
  }
  const xs = blocked.map(([x]) => x);
  const ys = blocked.map(([, y]) => y);
  const x0 = Math.min(...xs);
  const y0 = Math.min(...ys);
  const areaWidth = Math.max(...xs) - x0 + 1;
  const bitmap = new Uint8Array(areaWidth * (Math.max(...ys) - y0 + 1));
  for (const [x, y] of blocked) {
    bitmap[(y - y0) * areaWidth + (x - x0)] = 1;
  }
  return { x0: ox * cells + x0, y0: oy * cells + y0, width: areaWidth, height: bitmap.length / areaWidth, cells: bitmap };
}

/**
 * The footprint a behaviour prop blocks in a state, where the game blocks it: a thing by the footprint of the
 * frame its state shows, stamped from the anchor that frame is drawn at (`thingAnchor`); a prop driven by
 * presence by its initial state's footprint at its own anchor, which its chunk's collision carries whatever it
 * shows.
 *
 * @param prop The placed prop.
 * @param stateId The state the map shows it in, or null for its initial state.
 * @param context The compile context.
 * @param tileSize Tile size in pixels.
 * @returns The world collision cells it blocks, none when it stamps nothing or cannot be laid out.
 */
export function footprintArea(prop: PropInstance, stateId: string | null, context: CompileContext, tileSize: number): PresenceArea {
  const type = typeOf(prop, context);
  if (type === undefined) {
    return NOWHERE;
  }
  const flips = { flipX: prop.flipX, flipY: prop.flipY };
  if (type.inside !== undefined) {
    const footprint = initialFootprint(prop, context, tileSize);
    return footprint === null ? NOWHERE : stampedArea(footprint, { x: prop.x, y: prop.y, ...flips }, tileSize);
  }
  const frames = stateFrames(prop, type, context);
  const initial = frames.find((f) => f.state.id === type.initial);
  const shown = frames.find((f) => f.state.id === (stateId ?? type.initial));
  if (initial === undefined || shown === undefined) {
    return NOWHERE;
  }
  const footprint = stateFootprint(prop, shown, context, tileSize / COLLISION_CELLS_PER_TILE);
  if (footprint === null) {
    return NOWHERE;
  }
  const anchor = thingAnchor(prop, context, tileSize, footprint.w);
  return stampedArea(footprint, { x: anchor.x, y: anchor.y, ...flips }, tileSize);
}

/**
 * The stored cell under a world pixel: the column across the frame from its left edge, unmirrored for a
 * mirrored prop, and the row up from its base, so a cell painted there shows under the pixel.
 *
 * @param space Where the prop's cells lie.
 * @param x World pixel x.
 * @param y World pixel y.
 * @param cellPixels Pixels per collision cell.
 * @returns The cell, `x` its column and `y` its row up.
 */
export function frameCellAt(space: CellSpace, x: number, y: number, cellPixels: number): FrameCell {
  const shown = Math.floor((x - space.left) / cellPixels);
  return { x: space.mirrored ? space.columns - 1 - shown : shown, y: Math.ceil((space.bottom - y) / cellPixels) - 1 };
}

/**
 * Cells in a prop's own space as the world collision cells they cover where its look lays them, mirrored as
 * its stored cells are, so a stroke's running preview draws where its cells will once written.
 *
 * @param space Where the prop's cells lie.
 * @param cells The cells as `column,rowsUp` keys.
 * @param tileSize Tile size in pixels.
 * @returns The area.
 */
export function areaOfCells(space: CellSpace, cells: ReadonlySet<string>, tileSize: number): PresenceArea {
  const shown = [...cells].map((key): readonly [number, number] => {
    const [column, rowsUp] = key.split(",").map(Number) as [number, number];
    return [space.mirrored ? space.columns - 1 - column : column, rowsUp];
  });
  return shown.length === 0 ? NOWHERE : presenceArea({ x: space.left / tileSize, y: space.bottom / tileSize - 1 }, shown);
}

/**
 * A behaviour carrying painted cells, sorted by row then column as the cell editors write them; none leaves it
 * on its default cells.
 *
 * @param behaviour The behaviour.
 * @param cells The cells as `column,rowsUp` keys.
 * @returns The behaviour.
 */
export function withActivationCells(behaviour: PropBehaviour, cells: ReadonlySet<string>): PropBehaviour {
  const { activation: _previous, ...rest } = behaviour;
  const sorted = [...cells].map((key) => key.split(",").map(Number) as [number, number]).sort((a, b) => a[1] - b[1] || a[0] - b[0]);
  return sorted.length === 0 ? rest : { ...rest, activation: sorted };
}

/**
 * Every behaviour prop the Activation overlay draws: each on a shown prop layer, in document order, laid out
 * in the state the map shows it in, with its type and its worst issue (a refusal before a warning). A prop the
 * overlay cannot lay out is left out; its issue says why.
 *
 * @param sources What the entries are resolved from.
 * @returns The entries.
 */
export function activationEntries(sources: ActivationSources): ActivationEntry[] {
  const { map, context, rectOf, tileSize, shownState, issues, shown } = sources;
  const worst = new Map<string, "error" | "warning">();
  for (const issue of issues) {
    if (issue.layerId !== undefined && issue.propId !== undefined) {
      const key = `${issue.layerId}\u0000${issue.propId}`;
      worst.set(key, issue.severity === undefined || worst.get(key) === "error" ? "error" : "warning");
    }
  }
  const entries: ActivationEntry[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "prop" || !shown(layer.id)) {
      continue;
    }
    for (const prop of layer.props) {
      const type = prop.behaviour?.type;
      const look = type === undefined ? null : activationLook(prop, shownState(layer.id, prop), context, rectOf, tileSize);
      if (type !== undefined && look !== null) {
        entries.push({ layerId: layer.id, propId: prop.id, type, issue: worst.get(`${layer.id}\u0000${prop.id}`) ?? null, look });
      }
    }
  }
  return entries;
}
